import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const PageTabs = ({
                      totalPages,
                      pageSize,
                      test,
                      questionResults = [],
                      currentPage,
                      onPageChange,
                  }) => {
    const tabRefs = useRef([]);

    // Initialize refs array
    useEffect(() => {
        tabRefs.current = tabRefs.current.slice(0, totalPages);
    }, [totalPages]);

    const handleKeyDown = (event, index) => {
        const { key } = event;
        let newIndex = index;

        switch (key) {
            case 'ArrowRight':
            case 'ArrowDown':
                event.preventDefault();
                newIndex = (index + 1) % totalPages;
                tabRefs.current[newIndex].focus();
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                event.preventDefault();
                newIndex = (index - 1 + totalPages) % totalPages;
                tabRefs.current[newIndex].focus();
                break;
            case 'Home':
                event.preventDefault();
                tabRefs.current[0].focus();
                break;
            case 'End':
                event.preventDefault();
                tabRefs.current[totalPages - 1].focus();
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                onPageChange(index + 1);
                break;
            default:
                break;
        }
    };

    const tabs = [];

    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        const pageNumber = pageIndex + 1;
        const pageStartIndex = pageIndex * pageSize;
        const pageQuestions = test.testQuestionBeans.slice(
            pageStartIndex,
            pageStartIndex + pageSize
        );

        // Generate question result blocks
        const questionBlocks = pageQuestions.map((question) => {
            const questionResult = questionResults.find(
                (qResult) => qResult.questionId === question.questionId
            );

            let resultClass = '';
            if (questionResult) {
                if (questionResult.result === true) {
                    resultClass = 'ch-correct-bg';
                } else if (questionResult.hasUserResponse === true) {
                    resultClass = 'ch-incorrect-bg';
                } else {
                    resultClass = 'ch-unmarked-bg';
                }
            } else {
                resultClass = 'ch-unmarked-bg';
            }

            return (
                <div
                    className={`ch-page-question-result ${resultClass}`}
                    key={question.questionId}
                    data-question-id={question.questionId}
                    aria-label={`Question ${question.questionId} result`}
                ></div>
            );
        });

        // Fill in any missing question blocks to maintain consistent layout
        const missingCount = pageSize - pageQuestions.length;
        for (let k = 0; k < missingCount; k++) {
            questionBlocks.push(
                <div
                    className="ch-page-question-result ch-blank"
                    key={`blank-${pageNumber}-${k}`}
                    aria-hidden="true"
                ></div>
            );
        }

        // Determine if the current tab is selected
        const isSelected = currentPage === pageNumber;

        tabs.push(
            <button
                key={pageNumber}
                role="tab"
                aria-selected={isSelected}
                aria-controls={`tabpanel-${pageNumber}`}
                id={`tab-${pageNumber}`}
                className={`ch-page-tab ${isSelected ? 'ch-selected' : ''}`}
                data-page={pageNumber}
                ref={(el) => (tabRefs.current[pageIndex] = el)}
                tabIndex={isSelected ? 0 : -1}
                onClick={() => onPageChange(pageNumber)}
                onKeyDown={(e) => handleKeyDown(e, pageIndex)}
            >
                <div className="ch-page-label">Page&nbsp;{pageNumber}</div>
                <div className="ch-page">{questionBlocks}</div>
            </button>
        );
    }

    return (
        <div className="ch-page-tabs" role="tablist" aria-label="Page Tabs">
            {tabs}
            {tabs.map((tab, index) => (
                <div
                    key={`tabpanel-${index + 1}`}
                    role="tabpanel"
                    id={`tabpanel-${index + 1}`}
                    aria-labelledby={`tab-${index + 1}`}
                    hidden={currentPage !== index + 1}
                    tabIndex={0}
                >
                    {/* Content for each tab panel can be rendered here if needed */}
                </div>
            ))}
        </div>
    );
};

// Define PropTypes for better type checking
PageTabs.propTypes = {
    totalPages: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    test: PropTypes.shape({
        testQuestionBeans: PropTypes.arrayOf(
            PropTypes.shape({
                questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                    .isRequired,
                // Add other question properties if needed
            })
        ).isRequired,
    }).isRequired,
    questionResults: PropTypes.arrayOf(
        PropTypes.shape({
            questionId: PropTypes.number.isRequired,
            result: PropTypes.bool,
            hasUserResponse: PropTypes.bool,
        })
    ),
    currentPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
};

export default PageTabs;
